import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Navigate, useParams } from "react-router-dom";

import { PlaceEditForm } from "@/components/ui/admin/places/PlaceEditForm";
import {
  createEventService,
  createPlaceService,
  createTaxonomyService,
} from "@/core/services/factory";
import { getPlaceSharePath } from "@/core/services/types/place.types";
import { getLibraryReturnHref } from "@/lib/library-search-snapshot";
import { useTranslations } from "@/lib/useTranslations";

export function PlaceEditPage() {
  const { id } = useParams<{ id: string }>();
  const t = useTranslations("places.edit");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["place-edit", id],
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      if (!id) throw new Error("missing id");
      const places = createPlaceService();
      const events = createEventService();
      const taxonomy = createTaxonomyService();
      const [place, linkedEvents, placeTaxonomy, taxonomyData] =
        await Promise.all([
          places.getForEdit(id),
          events.listByPlaceId(id),
          places.getPlaceTaxonomy(id),
          taxonomy.listAll(),
        ]);
      return { place, linkedEvents, placeTaxonomy, taxonomyData };
    },
  });

  if (!id) return <Navigate to={getLibraryReturnHref("places")} replace />;
  if (isLoading && !data) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-1 items-center justify-center px-6 py-10 text-sm text-neutral-500">
        Chargement…
      </div>
    );
  }
  if (isError || !data?.place) {
    return <Navigate to={getLibraryReturnHref("places")} replace />;
  }

  const { place, linkedEvents, placeTaxonomy, taxonomyData } = data;
  const adminOrigin = import.meta.env.VITE_ADMIN_URL?.replace(/\/$/, "");
  const path = getPlaceSharePath(id);
  const shareUrl = adminOrigin ? `${adminOrigin}${path}` : path;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-8 overflow-y-auto px-6 py-10">
      <p className="text-sm font-medium uppercase tracking-widest text-neutral-500">
        {t("eyebrow")}
      </p>

      <PlaceEditForm
        key={place.id}
        place={place}
        linkedEvents={linkedEvents}
        shareUrl={shareUrl}
        taxonomy={placeTaxonomy}
        artForms={taxonomyData.artForms}
        artisticPeriods={taxonomyData.artisticPeriods}
        culturalAreas={taxonomyData.culturalAreas}
        exhibitionTypes={taxonomyData.exhibitionTypes}
      />
    </div>
  );
}
